<template>
  <div id="natcon-lom-award-edit" class="professional-award-form">
    <!-- Professional Overlay Loading -->
    <v-overlay :value="OverlayLoadingFlag" class="professional-overlay">
      <div class="loading-container">
        <v-progress-circular
          indeterminate
          size="64"
          width="4"
          color="#1976d2"
        ></v-progress-circular>
        <p class="loading-text mt-4">Processing your award application...</p>
      </div>
    </v-overlay>

    <!-- Professional Main Container -->
    <v-card class="main-form-container" elevation="4">
      <!-- Professional Header Section -->
      <v-card-title class="professional-form-header pa-6">
        <div class="header-content">
          <div class="title-section">
            <div class="main-title-wrapper">
              <v-icon size="32" color="#1976d2" class="mr-3">mdi-trophy</v-icon>
              <div class="title-text">
                <h1 class="form-main-title">{{ PageTitle }}</h1>
                <div v-if="PageDescription" class="subtitle">
                  {{ PageDescription }}
                </div>
              </div>
            </div>
          </div>

          <div class="action-buttons">
            <router-link to="/national/lom-award/list">
              <v-btn
                color="#ff9800"
                elevation="2"
                rounded
                class="action-btn mr-3 white--text"
              >
                <v-icon left size="18">mdi-arrow-left</v-icon>
                Back
              </v-btn>
            </router-link>

            <v-btn
              @click.prevent="refreshPageData"
              color="#2ecc71"
              elevation="2"
              rounded
              class="action-btn white--text"
            >
              <v-icon left size="18">mdi-refresh</v-icon>
              Refresh
            </v-btn>
          </div>
        </div>
      </v-card-title>

      <!-- Professional Content Container -->
      <v-card-text class="form-main-content pa-6">
        <!-- Professional Loading Section -->
        <div class="form-loading-section" v-if="LoadingFlag">
          <div class="loading-wrapper">
            <v-progress-circular
              indeterminate
              color="#1976d2"
              size="48"
              width="4"
            ></v-progress-circular>
            <p class="loading-text mt-4">Loading award data...</p>
          </div>
        </div>

        <!-- Award Detail Section -->
        <v-container v-if="row != ''">
          <v-card class="award-detail-card mb-6" elevation="3">
            <v-card-title class="award-detail-header">
              <v-icon size="24" color="#1976d2" class="mr-3"
                >mdi-information-outline</v-icon
              >
              <span class="section-title">Award Details</span>
            </v-card-title>
            <v-card-text class="pa-4">
              <v-row>
                <v-col cols="12" lg="4" sm="12">
                  <div class="detail-item">
                    <div class="detail-label">Award Status</div>
                    <div class="detail-value">
                      <v-chip
                        :color="row.AwardStatusColor"
                        dark
                        class="status-chip"
                        >{{ row.AwardStatusTxt }}</v-chip
                      >
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" lg="4" sm="12">
                  <div class="detail-item">
                    <div class="detail-label">Award Register Number</div>
                    <div class="detail-value">
                      {{ row.AwardRegisterNumber }}
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" lg="4" sm="12">
                  <div class="detail-item">
                    <div class="detail-label">Award Title</div>
                    <div class="detail-value">{{ row.AwardTypeTitleTxt }}</div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- NatCon Registration Status Alert -->
          <v-row>
            <v-col cols="12">
              <!-- success state when member is registered for NatCon -->
              <template v-if="NatconRegistrationFlag">
                <!-- SUCCESS STATE: LOM Group Eligible -->
                <v-card
                  class="natcon-alert-card pa-3 elevation-1 mt-3"
                  style="
                    background: rgba(0, 200, 83, 0.12);
                    border: 1px solid rgba(0, 200, 83, 0.3);
                  "
                >
                  <v-row align="center" no-gutters>
                    <v-col cols="auto">
                      <v-avatar size="56" class="mr-4" color="green lighten-5">
                        <v-icon color="green darken-2" size="28"
                          >mdi-check-circle</v-icon
                        >
                      </v-avatar>
                    </v-col>

                    <v-col>
                      <div
                        class="natcon-alert-title"
                        style="font-weight: 600; color: #1b5e20"
                      >
                        NatCon Registration Verified
                      </div>

                      <div class="natcon-alert-msg" style="color: #2e7d32">
                        This LO has successfully
                        <strong>met the eligibility criteria</strong> for the
                        NatCon Registration.

                        <div
                          class="natcon-alert-extra mt-2"
                          style="color: #2e7d32"
                        >
                          You may continue with the LO Award nomination process.
                        </div>
                      </div>
                    </v-col>

                    <v-col cols="auto" class="d-none d-sm-flex justify-end">
                      <v-chip
                        outlined
                        small
                        color="green lighten-5"
                        text-color="green darken-2"
                      >
                        Eligible
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-card>
              </template>

              <!-- error state when not registered (keeps existing look + extra line) -->
              <template v-else>
                <!-- ERROR STATE: LOM Group NOT Eligible -->
                <v-card class="natcon-alert-card pa-3 elevation-1 mt-3">
                  <v-row align="center" no-gutters>
                    <v-col cols="auto">
                      <v-avatar
                        size="56"
                        class="mr-4 pulse-icon"
                        color="red lighten-5"
                      >
                        <v-icon color="red darken-2" size="28"
                          >mdi-account-alert</v-icon
                        >
                      </v-avatar>
                    </v-col>

                    <v-col>
                      <div class="natcon-alert-title">
                        NatCon Registration Required
                      </div>

                      <div class="natcon-alert-msg">
                        This LO has
                        <strong>not met the eligibility requirements</strong>
                        for the NatCon Registration. Nomination cannot proceed
                        until all mandatory conditions are completed.

                        <div class="natcon-alert-extra mt-2">
                          This LO must have at least one NatCon Registration to
                          proceed with the nomination process.
                        </div>
                      </div>
                    </v-col>

                    <v-col cols="auto" class="d-none d-sm-flex justify-end">
                      <v-chip
                        outlined
                        small
                        color="red lighten-5"
                        text-color="red darken-2"
                      >
                        Not Eligible
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-card>
              </template>
            </v-col>
          </v-row>
          <br />

          <jci-india-scoring-table
            v-if="row.AwardCategoryId > 0"
            :AwardCategoryId="row.AwardCategoryId"
            :AwardTypeId="row.AwardTypeId"
            :LomId="row.LomId"
            class="scoring-component"
          ></jci-india-scoring-table>

          <br />
        </v-container>

        <!-- Professional Form Container -->
        <v-container>
          <v-form
            ref="form1"
            v-model="valid1"
            lazy-validation
            v-on:submit.prevent="getTotalNatConLomAwardsDetails"
            class="professional-form"
          >
            <div class="form-content-wrapper">
              <!-- Professional Judging Criteria Section -->
              <v-card class="criteria-card mb-6" elevation="3" v-if="row != ''">
                <v-card-title class="criteria-header">
                  <v-icon size="24" color="#1976d2" class="mr-3"
                    >mdi-clipboard-text</v-icon
                  >
                  <span class="section-title">Judging Criteria</span>
                </v-card-title>
                <v-card-text class="pa-6">
                  <!-- Empty State for No Criteria -->
                  <div v-if="row == ''" class="empty-state text-center pa-4">
                    <h5 class="text-center text-dark">
                      No criteria available.
                    </h5>
                  </div>

                  <!-- Professional Criteria Questions -->
                  <div
                    class="criteria-question-section mb-6"
                    v-for="(item, index) in row.AwardAnswersArray"
                    :key="index"
                  >
                    <!-- Criteria Detail Header -->
                    <div
                      class="criteria-detail-header mb-4"
                      v-if="item.CriteriaTitleFlag"
                    >
                      <div class="criteria-detail-card">
                        <div class="criteria-detail-content">
                          <h3 class="criteria-title">
                            {{ item.CriteriaTitle }}
                            <small>(score - {{ item.CriteriaScore }})</small>
                          </h3>
                        </div>
                      </div>
                    </div>

                    <!-- Question Content -->
                    <div class="question-content-wrapper">
                      <div class="question-label-section mb-3">
                        <h3 class="question-title">
                          <span class="required-indicator">*</span>
                          {{ item.QuestionsText }}
                        </h3>
                      </div>

                      <div class="question-input-section mb-4">
                        <div class="html-editor-wrapper">
                          <vue-editor
                            :id="item.AnswerId"
                            v-model="item.AnswerText"
                            :editorOptions="editorOptions"
                            @text-change="onTextChange($event, item)"
                            placeholder="Please provide your detailed answer here..."
                            class="professional-editor"
                          ></vue-editor>
                          <div
                            class="character-counter mt-1 text-right"
                            :class="{
                              'error--text':
                                getCharacterCount(item.AnswerText) >=
                                item.QuestionsCharsCount,
                            }"
                          >
                            <strong>{{
                              getCharacterCount(item.AnswerText)
                            }}</strong>
                            / {{ item.QuestionsCharsCount }}
                            <span
                              v-if="
                                getCharacterCount(item.AnswerText) >=
                                item.QuestionsCharsCount
                              "
                              class="limit-message"
                            >
                              (Character limit reached)
                            </span>
                          </div>
                        </div>
                      </div>

                      <div class="attachments-section mt-5">
                        <div
                          class="attachments-header d-flex justify-space-between align-center"
                        >
                          <h3 class="attachments-title">
                            <v-icon size="20" color="#ff9800" class="mr-2"
                              >mdi-paperclip</v-icon
                            >
                            Attachments
                            <small
                              >(Max: {{ item.QuestionsAttachmentMax }})</small
                            >
                          </h3>

                          <v-btn
                            color="primary"
                            fab
                            small
                            elevation="2"
                            class="add-attachment-btn"
                            v-if="
                              item.QuestionsAttachmentMax >
                              item.EventDetails.length
                            "
                            @click="addNewRow(item)"
                          >
                            <v-icon>mdi-plus</v-icon>
                          </v-btn>
                        </div>

                        <div
                          class="attachments-grid mt-4"
                          v-if="
                            item.EventDetails && item.EventDetails.length > 0
                          "
                        >
                          <v-row>
                            <v-col
                              cols="12"
                              sm="6"
                              md="4"
                              lg="3"
                              v-for="(data, idx) in item.EventDetails.filter(
                                (d) => d.EventId !== ''
                              )"
                              :key="idx"
                            >
                              <v-card class="attachment-card" elevation="2">
                                <div class="attachment-actions">
                                  <v-btn
                                    icon
                                    color="error"
                                    small
                                    @click="deleteRow(item.SNo, idx)"
                                    ><v-icon>mdi-delete</v-icon></v-btn
                                  >
                                </div>

                                <v-card-text class="pa-4">
                                  <div class="event-images text-center mb-3">
                                    <viewer
                                      rebuild
                                      :images="data.EventImagesArray"
                                    >
                                      <img
                                        class="event-image"
                                        width="100"
                                        height="100"
                                        :src="src"
                                        :key="src"
                                        v-for="src in data.EventImagesArray"
                                      />
                                    </viewer>
                                  </div>

                                  <div class="event-details">
                                    <div class="detail-row mb-2">
                                      <v-icon
                                        size="16"
                                        color="#666"
                                        class="mr-1"
                                        >mdi-calendar</v-icon
                                      ><span class="detail-label"
                                        >Event Date:</span
                                      ><span class="detail-value">{{
                                        data.EventDate
                                      }}</span>
                                    </div>
                                    <div class="detail-row mb-2">
                                      <v-icon
                                        size="16"
                                        color="#666"
                                        class="mr-1"
                                        >mdi-tag</v-icon
                                      ><span class="detail-label"
                                        >Event Name:</span
                                      ><span class="detail-value">{{
                                        data.EventName
                                      }}</span>
                                    </div>
                                  </div>
                                </v-card-text>
                              </v-card>
                            </v-col>
                          </v-row>
                        </div>

                        <div class="no-attachments-state mt-4" v-else>
                          <v-alert
                            type="info"
                            text
                            color="grey lighten-1"
                            outlined
                            dense
                            >No attachments added yet. Click the + button to add
                            an attachment.</v-alert
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
              <template v-if="row != ''">
                <!-- Professional Additional Images Section -->
                <v-card class="attachments-additional-card mb-6 elevation-3">
                  <v-card-title class="attachments-additional-header">
                    <v-icon size="24" color="#ff9800"
                      >mdi-image-multiple</v-icon
                    >
                    <span class="section-title">Additional Images</span>
                    <v-spacer></v-spacer>
                    <v-btn
                      v-if="AllProjectMax > AllProjectCount"
                      @click="addAttachmentNewRow()"
                      icon
                      small
                      color="#ff9800"
                      class="add-attachment-action-btn"
                    >
                      <v-icon>mdi-plus-circle</v-icon>
                    </v-btn>
                  </v-card-title>

                  <v-card-text class="pa-6">
                    <v-alert type="info" class="info-alert mb-4">
                      <v-icon color="#ff9800" class="mr-2"
                        >mdi-information-variant</v-icon
                      >
                      <strong>Image Requirements:</strong> JPG/PNG/JPEG format,
                      maximum 2MB per image
                    </v-alert>

                    <!-- Empty State -->
                    <div
                      v-if="
                        row.AnswerAttachmentOfficers.length === 0 &&
                        AllProject.length === 0
                      "
                      class="empty-state-container"
                    >
                      <svg
                        width="80"
                        height="80"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#ff9800"
                        stroke-width="1.5"
                      >
                        <path
                          d="M21 19V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2z"
                        ></path>
                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                        <polyline points="21 15 16 10 5 21"></polyline>
                      </svg>
                      <p class="mt-3">
                        No images added yet. Click the + button to add images.
                      </p>
                    </div>

                    <!-- Image Gallery -->
                    <v-row
                      v-if="
                        row.AnswerAttachmentOfficers.length > 0 ||
                        AllProject.length > 0
                      "
                      class="image-gallery mt-3"
                    >
                      <!-- Existing attachments from row -->
                      <v-col
                        v-for="(data, index) in row.AnswerAttachmentOfficers"
                        cols="12"
                        sm="6"
                        md="4"
                        lg="3"
                        :key="'existing-' + index"
                      >
                        <v-card class="image-card">
                          <div class="image-delete-action">
                            <v-btn
                              @click="deleteRow1(index)"
                              icon
                              x-small
                              color="white"
                              class="delete-btn"
                            >
                              <v-icon>mdi-close-circle</v-icon>
                            </v-btn>
                          </div>
                          <div class="image-preview">
                            <viewer rebuild :images="data.EventImagesArray">
                              <v-img
                                v-for="src in data.EventImagesArray"
                                :key="src"
                                :src="src"
                                aspect-ratio="1"
                              ></v-img>
                            </viewer>
                          </div>
                          <v-card-text class="image-details pa-3">
                            <div class="detail-item mb-2">
                              <v-icon size="14" color="#ff9800"
                                >mdi-calendar</v-icon
                              >
                              <span class="detail-label">Date:</span>
                              <span class="detail-value">{{
                                data.EventDate
                              }}</span>
                            </div>
                            <div class="detail-item">
                              <v-icon size="14" color="#ff9800">mdi-tag</v-icon>
                              <span class="detail-label">Event:</span>
                              <span class="detail-value">{{
                                data.EventName
                              }}</span>
                            </div>
                          </v-card-text>
                        </v-card>
                      </v-col>

                      <!-- New attachments from AllProject -->
                      <v-col
                        v-for="(data1, k) in AllProject"
                        cols="12"
                        sm="6"
                        md="4"
                        lg="3"
                        :key="'new-' + k"
                      >
                        <v-card class="image-card">
                          <div class="image-delete-action">
                            <v-btn
                              @click="deleteAttachmentRow(k)"
                              icon
                              x-small
                              color="white"
                              class="delete-btn"
                            >
                              <v-icon>mdi-close-circle</v-icon>
                            </v-btn>
                          </div>
                          <div class="image-preview">
                            <viewer rebuild :images="data1.AnswerEventImageUrl">
                              <v-img
                                v-for="src in data1.AnswerEventImageUrl"
                                :key="src"
                                :src="src"
                                aspect-ratio="1"
                              ></v-img>
                            </viewer>
                          </div>
                          <v-card-text class="image-details pa-3">
                            <div class="detail-item mb-2">
                              <v-icon size="14" color="#ff9800"
                                >mdi-calendar</v-icon
                              >
                              <span class="detail-label">Date:</span>
                              <span class="detail-value">{{
                                data1.AnswerEventDate
                              }}</span>
                            </div>
                            <div class="detail-item">
                              <v-icon size="14" color="#ff9800">mdi-tag</v-icon>
                              <span class="detail-label">Event:</span>
                              <span class="detail-value">{{
                                data1.AnswerEventName
                              }}</span>
                            </div>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </template>

              <!-- Professional Video Upload Section -->
              <v-card class="video-upload-card mb-6 elevation-3">
                <v-card-title class="video-upload-header">
                  <v-icon size="24" color="#d32f2f">mdi-video</v-icon>
                  <span class="section-title">Video Submission</span>
                </v-card-title>

                <v-card-text class="pa-6">
                  <!-- Existing Video Display -->
                  <v-row v-if="row.AwardVideoPath != null" class="mb-6">
                    <v-col cols="12" sm="12" lg="6" md="6">
                      <h6 class="blue--text mb-4">
                        <v-icon size="16">mdi-video-check</v-icon> Current Video
                      </h6>
                      <video
                        :src="row.AwardVideoPath"
                        width="100%"
                        controls
                        style="border-radius: 8px"
                      ></video>
                    </v-col>
                  </v-row>

                  <v-alert type="warning" class="video-info-alert mb-4">
                    <v-icon color="#d32f2f" class="mr-2"
                      >mdi-alert-circle-outline</v-icon
                    >
                    <strong>Video Requirements:</strong> MP4, AVI, or MOV
                    format, maximum 25MB
                  </v-alert>

                  <!-- Video Upload Section -->
                  <div class="upload-wrapper">
                    <div class="upload-label">
                      <v-icon size="18" color="#d32f2f" class="mr-2"
                        >mdi-video-plus</v-icon
                      >
                      Upload Video <small>(optional)</small>
                    </div>
                    <v-file-input
                      ref="UploadVideo"
                      v-model="UploadVideo"
                      counter
                      show-size
                      truncate-length="47"
                      :clearable="false"
                      accept="video/*"
                      label="Choose a video file"
                      class="video-input"
                    >
                      <template v-slot:append-outer>
                        <v-icon
                          v-if="UploadVideo && UploadVideo.name"
                          color="#4caf50"
                          @click="resetUploadVideo"
                        >
                          mdi-close-circle
                        </v-icon>
                      </template>
                    </v-file-input>
                  </div>

                  <!-- Video Preview Info -->
                  <div
                    v-if="UploadVideo && UploadVideo.name"
                    class="video-preview-info mt-3"
                  >
                    <v-chip color="#4caf50" text-color="white" small>
                      <v-icon left size="16">mdi-check-circle</v-icon>
                      {{ UploadVideo.name }}
                    </v-chip>
                  </div>
                </v-card-text>
              </v-card>

              <v-row wrap v-if="ProgessStart == 1">
                <v-col align="center" cols="12" md="12">
                  <v-progress-circular
                    :rotate="180"
                    :size="100"
                    :width="15"
                    :value="uploadPercentage"
                    color="#00a4ef"
                  >
                    {{ uploadPercentage }}
                  </v-progress-circular>
                  <h3 class="red--text">
                    Please wait, we are uploading your details. Time may vary
                    based on your internet connection.
                  </h3>
                </v-col>
              </v-row>
              <!-- Professional Submit Section -->
              <div class="submit-section" v-if="row != ''">
                <v-card class="submit-card" elevation="3">
                  <v-card-text class="text-center pa-6">
                    <div class="submit-info mb-4">
                      <h3 class="submit-title">Ready to Save Changes?</h3>
                      <p class="submit-description">
                        You can save your progress or submit your final
                        application.
                      </p>
                    </div>

                    <div class="action-buttons">
                      <v-btn
                        @click.prevent="getTotalNatConLomAwardsDetails(4)"
                        :loading="SaveFlag"
                        :disabled="!NatconRegistrationFlag"
                        elevation="3"
                        rounded
                        large
                        color="#4caf50"
                        class="submit-btn white--text mr-4"
                      >
                        <v-icon left>mdi-content-save</v-icon>
                        Save Draft
                      </v-btn>

                      <v-btn
                        :disabled="!valid1 || !NatconRegistrationFlag"
                        @click.prevent="getTotalNatConLomAwardsDetails(3)"
                        :loading="SubmitFlag"
                        elevation="3"
                        rounded
                        large
                        color="#1976d2"
                        class="submit-btn white--text"
                      >
                        <v-icon left>mdi-send</v-icon>
                        Final Submit
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>
              </div>
            </div>
          </v-form>

          <v-row wrap v-if="ProgessStart == 1">
            <v-col align="center" cols="12" md="12">
              <v-progress-circular
                :rotate="180"
                :size="100"
                :width="15"
                :value="uploadPercentage"
                color="#00a4ef"
                >{{ uploadPercentage }}</v-progress-circular
              >
              <h3 class="red--text">
                Please wait, we are uploading your details. Time may vary based
                on your internet connection.
              </h3>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>

    <!-- Professional Dialogs -->
    <v-dialog v-model="addRecordPrompt" persistent max-width="70%">
      <answer-attachment-edit
        pageTitle="Add Project Details"
        :recordData="selectedRecord"
        :addRecordPrompt="addRecordPrompt"
        @hideAddRecordPrompt="hideAddRecordPrompt($event)"
        v-if="addRecordPrompt"
      ></answer-attachment-edit>
    </v-dialog>

    <v-dialog v-model="addAttachmentRecordPrompt" persistent max-width="70%">
      <attachment-edit
        pageTitle="Add Project Details"
        :addRecordPrompt="addAttachmentRecordPrompt"
        @hideAddRecordPrompt="hideAddAttachmentRecordPrompt($event)"
        v-if="addAttachmentRecordPrompt"
      ></attachment-edit>
    </v-dialog>
  </div>
</template>
<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";
import AnswerAttachmentEdit from "@/view/pages/erp/jci-award/natcon/common/AnswerAttachmentEdit.vue";
import AttachmentEdit from "@/view/pages/erp/jci-award/natcon/common/AttachmentEdit.vue";
import { VueEditor } from "vue2-editor"; // Import Vue-Quill-Editor

export default {
  mixins: [common],
  components: {
    AnswerAttachmentEdit,
    AttachmentEdit,
    VueEditor,
  },
  data() {
    return {
      ProgessStart: 0,
      uploadPercentage: 0,
      OverlayLoadingFlag: false,

      valid1: true,
      valid2: true,
      LoadingFlag: false,
      SaveFlag: false,
      SubmitFlag: false,
      SearchFlag: false,
      CardLoadingFlag: false,
      ResultFlag: false,
      UploadFlag: false,
      addButtonFlag: true,

      Status: "",

      NatconRegistrationFlag: false,
      NatconLomAwardStartDate: "",
      NatconLomAwardEndDate: "",
      TotalNatconLomAward: 0,
      TotalApplied: 0,
      TotalPartiallySaved: 0,
      editorOptions: {
        modules: {
          toolbar: [
            ["bold", "italic", "underline", "strike"],
            ["blockquote"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ indent: "-1" }, { indent: "+1" }],
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [{ color: [] }, { background: [] }],
            ["clean"],
          ],
        },
        placeholder: "Please provide your detailed answer here...",
      },
      StartDateFlag: false,
      EndDateFlag: false,

      rows: {},

      AwardId: "",
      AwardTypeId: "",

      UploadVideo: [],
      UploadVideoRules: [
        (value) =>
          !value ||
          value.size < 25000000 ||
          "Upload Video size should be less than 25 MB!",
      ],
      row: [],
      // CriteriaAnswers: [],
      Project: [],
      AllImages: [],
      AllProject: [],
      AllProjectMax: 10,
      AllProjectCount: 0,
      addAttachmentRecordPrompt: false,

      selectedRecord: "",
      Id: "",
      showTextBox: false,

      addRecordPrompt: false,

      ImageUploadFlag: false,

      AnswerRules: [(v) => !!v || "Answer is required"],

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
    };
  },
  computed: {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        var flag = tr.PagePermissionFlag;
        console.log("PagePermissionFlag=" + flag);

        if (!flag) {
          this.$router.push("/access-denied");
        }

        var zone_name = this.$session.get("ZoneName");
        zone_name = zone_name == (null || undefined) ? 0 : zone_name;

        this.PageTitle = tr.PageTitle + " - " + zone_name;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        this.CurrentYearId = CurrentJciYearId;

        this.pageData();
      }
    },
  },
  mounted() {},
  methods: {
    // HTML Editor related methods
    getCharacterCount(html) {
      if (!html) return 0;
      // Remove HTML tags and entities to count only visible text
      const text = html.replace(
        /<[^>]*>|&nbsp;|&amp;|&lt;|&gt;|&quot;|&#39;/g,
        ""
      );
      return text.length;
    },

    onTextChange(event, item) {
      // Get the Quill editor instance from the event
      const editor = event.editor;
      const maxLength = item.QuestionsCharsCount;

      // Calculate the current text length without HTML tags
      const currentLength = this.getCharacterCount(item.AnswerText);

      // If the length exceeds the limit, prevent further input
      if (currentLength > maxLength) {
        // Get the current selection
        const range = editor.getSelection();

        // If there is no selection, we need to restore the previous text
        if (range && range.length === 0) {
          // Store current cursor position
          const cursorPosition = range.index;

          // Use DOM manipulation to truncate the HTML content
          const editorRoot = editor.root;
          const editorContent = editorRoot.innerHTML;

          // Create a temporary div to manipulate the HTML
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = editorContent;

          // Function to recursively process text nodes
          const processNode = (node, currentCount) => {
            if (currentCount >= maxLength) {
              // Remove node completely if we're already at the limit
              if (node.parentNode) {
                node.parentNode.removeChild(node);
              }
              return currentCount;
            }

            if (node.nodeType === Node.TEXT_NODE) {
              const nodeLength = node.textContent.length;
              if (currentCount + nodeLength > maxLength) {
                // Truncate this text node
                const remainingChars = maxLength - currentCount;
                node.textContent = node.textContent.substring(
                  0,
                  remainingChars
                );
                return maxLength;
              } else {
                return currentCount + nodeLength;
              }
            } else if (node.nodeType === Node.ELEMENT_NODE) {
              // Skip certain elements that don't contribute to visible text
              if (node.tagName === "SCRIPT" || node.tagName === "STYLE") {
                return currentCount;
              }

              // Process child nodes
              let count = currentCount;
              const childNodes = Array.from(node.childNodes);
              for (const childNode of childNodes) {
                count = processNode(childNode, count);
                if (count >= maxLength) break;
              }
              return count;
            }

            return currentCount;
          };

          // Process the content
          processNode(tempDiv, 0);

          // Update the editor content
          editor.setContents(editor.clipboard.convert(tempDiv.innerHTML));

          // Restore cursor position, but don't go beyond the new content
          const newLength = editor.getText().length;
          const newPosition = Math.min(cursorPosition, newLength - 1);
          editor.setSelection(newPosition > 0 ? newPosition : 0);
        }
      }

      // Update validation rules
      if (currentLength >= maxLength) {
        if (!this.AnswerRules.includes(this.validateMaxLength)) {
          this.AnswerRules.push(this.validateMaxLength);
        }
      } else {
        const index = this.AnswerRules.indexOf(this.validateMaxLength);
        if (index !== -1) {
          this.AnswerRules.splice(index, 1);
        }
      }
    },

    validateMaxLength(v) {
      // This is a custom validation rule for the rich text editor
      // It will be used by Vuetify's validation system
      const item = this.row.AwardAnswersArray.find(
        (item) => item.AnswerText === v
      );
      if (item && this.getCharacterCount(v) > item.QuestionsCharsCount) {
        return "Character limit exceeded";
      }
      return true;
    },
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "national_lom_award",
        Action: "edit",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    pageData() {
      console.log("pageData called");
      var id = this.$route.query.id;
      console.log({ id });
      this.AwardId = id;

      this.getTableRecords();
    },
    resetUploadVideo() {
      console.log("resetUploadVideo called.");
      this.UploadVideo = "";
    },
    resetForm() {
      console.log("resetForm called");
      var rows = this.CriteriaAnswers;

      rows.map((row, index) => {
        row.AnswerText = "";
        row.AllProject = {};
      });

      this.getTableRecords();
    },
    addNewRow(tr) {
      console.log("addNewRow called");
      console.log({ tr });
      this.selectedRecord = tr;
      this.addRecordPrompt = true;
    },
    deleteRow(id, index) {
      console.log("deleteRow called");
      console.log({ id });
      console.log({ index });
      if (index > -1) {
        var row = this.row.AwardAnswersArray;

        row.forEach((data, k) => {
          console.log({ k });
          if (id == k) {
            data.EventDetails.splice(index, 1);
          }
        });

        console.log("row = ", this.row);
      }
    },
    hideAddRecordPrompt(tr) {
      console.log("hideAddRecordPrompt called");
      this.addRecordPrompt = false;
      console.log({ tr });

      if (tr.EventId != "") {
        // this.Id = tr.Index;
        console.log("tr.index = ", tr.Index);

        var row = this.row.AwardAnswersArray;

        row.forEach((data, index) => {
          console.log("SNo = ", data.SNo);

          if (tr.Index == data.SNo) {
            console.log("Limit not Crossed");

            var n1 = data.QuestionsAttachmentMax;
            var n2 = data.EventDetails.length;
            console.log({ n1 }, { n2 });

            if (n1 > n2) {
              console.log("Limit not Crossed");

              data.EventDetails.push({
                EventId: tr.EventId,
                EventName: tr.EventName,
                EventDate: tr.EventDate,
                EventImagesArray: tr.EventPhoto,
                AnswerEventImages: tr.EventImages,
                AnswerInvitationImages: tr.InvitationImages,
                AnswerJciEventDescription: tr.EventDescription,
              });
            } else {
              console.log("Maximum Limit Crossed");
              var Message = "Attachment Maximum Count reached its limit.";
              this.sweetAlert("error", Message, false);
            }
          }
        });

        console.log("row = ", this.row);
      }
    },
    deleteRow1(id) {
      console.log("deleteRow1 called");
      console.log({ id });

      this.row.AnswerAttachmentOfficers.splice(id, 1);

      this.calculateAllProjectCount();
    },

    calculateAllProjectCount() {
      console.log("calculateAllProjectCount called");
      this.AllProjectCount =
        this.row.AnswerAttachmentOfficers.length + this.AllProject.length;
      console.log("AllProjectCount =", this.AllProjectCount);
    },
    addAttachmentNewRow() {
      console.log("addAttachmentNewRow called");
      this.addAttachmentRecordPrompt = true;
    },
    deleteAttachmentRow(id) {
      console.log("deleteAttachmentRow called");
      console.log({ id });

      this.AllProject.splice(id, 1);
      this.AllImages.splice(id, 1);
    },
    hideAddAttachmentRecordPrompt(tr) {
      console.log("hideAddAttachmentRecordPrompt called");
      this.addAttachmentRecordPrompt = false;
      console.log({ tr });

      if (tr.EventName != "") {
        var count = this.AllProject.length;
        var id = count == 0 ? 0 : count + 1;

        this.AllProject.push({
          AnswerEventId: "",
          AnswerEventName: tr.EventName,
          AnswerEventDate: tr.EventDate,
          AnswerEventDescription: tr.EventDescription,
          AnswerEventImageName: tr.EventImageName,
          AnswerEventImageUrl: tr.EventImageUrl,
          AnswerEventImages: tr.EventImages,
          AnswerInvitationImages: tr.InvitationImages,
        });

        this.AllImages.push({
          AnswerEventId: "",
          AnswerEventName: tr.EventName,
          UploadedImage: tr.EventImage.UploadedImage,
        });
      }
      console.log("AllProject = " + JSON.stringify(this.AllProject));
    },
    getTableRecords() {
      console.log("getTableRecords called");

      var AwardId = this.AwardId;
      console.log({ AwardId });

      if (AwardId > 0) {
        this.LoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/award/show";
        var upload = {
          UserInterface: 1,
          Award: AwardId,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.LoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              console.log("records=" + JSON.stringify(records));
              thisIns.row = records;
              thisIns.NatconRegistrationFlag = records.NatconRegistrationFlag;

              // thisIns.toast("success", output);
            } else {
              // thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.toast("error", error);
            thisIns.LoadingFlag = false;
          });
      } else {
        var message = "Award details not found.";
        console.log("error=" + message);
      }
    },
    getTotalNatConLomAwardsDetails(status) {
      console.log("getTotalNatConLomAwardsDetails called");

      var validate1 = true;
      console.log({ validate1 });

      console.log({ status });
      this.Status = status;

      if (validate1) {
        this.CardLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/award-type/total-awards";

        var AwardCategory = 4;
        var AwardGroup = 2;

        if (this.RoleId == 2) {
          // For Admin
          var upload = {
            UserInterface: 1,
            AwardCategory: AwardCategory,
            AwardGroup: AwardGroup,
            Lom: this.LomCode,
          };
        } else {
          // For Others
          var upload = {
            UserInterface: 1,
            AwardCategory: AwardCategory,
            AwardGroup: AwardGroup,
          };
        }

        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.CardLoadingFlag = false;

            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              console.log("records = " + JSON.stringify(records));
              thisIns.rows = records;
              thisIns.NatconLomAwardStartDate = records.StartDateTxt;
              thisIns.NatconLomAwardEndDate = records.EndDateTxt;
              thisIns.TotalNatconLomAward = records.TotalAward;
              thisIns.TotalApplied = records.TotalApplied;
              thisIns.TotalPartiallySaved = records.TotalPartiallySaved;
              thisIns.StartDateFlag = records.StartDateFlag;
              thisIns.EndDateFlag = records.EndDateFlag;

              console.log(
                "StartDateFlag=" +
                  records.StartDateFlag +
                  ", EndDateFlag=" +
                  records.EndDateFlag
              );

              if (thisIns.StartDateFlag && thisIns.EndDateFlag) {
                thisIns.confirmAlert(status);
              } else {
                thisIns.deadlineAlert();
              }
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            thisIns.CardLoadingFlag = false;
            console.log("error=" + error);
            thisIns.toast("error", error);
          });
      } else {
        var message = "";
        console.log("error=" + message);
      }
    },
    deadlineAlert() {
      console.log("deadlineAlert called");
      var htmlTxt = "";
      htmlTxt +=
        "<h3> Natcon Lom Awards Bidding ended at <b> " +
        this.NatconLomAwardEndDate +
        " </b> </h3>";
      // htmlTxt += "<br/> Start <b> " + this.NatconLomAwardEndDate + " </b>";

      Swal.fire({
        title: "Sorry !",
        text: "",
        icon: "warning",
        html: htmlTxt,
      });
    },
    confirmAlert(status) {
      console.log("confirmAlert called");

      var validate1 = false;

      validate1 = status == 4 ? true : this.$refs.form1.validate();
      console.log({ status }, { validate1 });

      if (validate1) {
        Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: `Save`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          if (result.isConfirmed) {
            this.submitForm(status);
          } else {
            this.sweetAlert("error", "You cancelled the process", false);
          }
        });
      } else {
        var message = "";
        if (!validate1) {
          message += "kindly answer all the questions.";
        }

        Swal.fire({
          // title: "Are you sure?",
          icon: "error",
          html: message,
        });
        // this.sweetAlert("error", message, false);
      }
    },
    submitForm(status) {
      console.log("submitForm is called");
      console.log({ status });

      this.ProgessStart = 1;
      this.OverlayLoadingFlag = true;
      if (status == 4) {
        this.SaveFlag = true;
      } else {
        this.SubmitFlag = true;
      }

      var count = this.AllImages.length;

      var video = this.UploadVideo != {} ? this.UploadVideo : {};
      console.log({ video });

      console.log(
        "AnswerAttachmentOfficers = ",
        this.row.AnswerAttachmentOfficers
      );

      this.AllImages.forEach((row, index) => {
        console.log({ index });
        row.AnswerEventId = index;
        console.log("AllImages=" + JSON.stringify(this.AllImages));
      });

      this.AllProject.forEach((item, index) => {
        console.log({ index });
        item.AnswerEventId = index;
        console.log("AllProject=" + JSON.stringify(this.AllProject));
      });

      var inputData = {
        ActiveStatus: status,
        Award: this.AwardId,
        CriteriaAnswers: this.row,
        AnswerAttachmentOfficers: this.row.AnswerAttachmentOfficers,
        AllProject: this.AllProject,
        AllImagesCount: count,
      };
      console.log("inputData=" + JSON.stringify(inputData));

      let upload = new FormData();
      upload.append("UserInterface", 1);
      upload.append("InputData", JSON.stringify(inputData));
      upload.append("UploadVideo", video);

      this.AllImages.forEach((row, index) => {
        var id = index + 1;
        console.log({ id });
        var UploadedImage = row["UploadedImage"];
        var EventId = row["AnswerEventId"];
        console.log({ UploadedImage }, { EventId });

        upload.append("AllImages" + index, UploadedImage, EventId);
      });

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;

      var add_url = server_url + "api/award/national-award-update";
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      const thisIns = this;
      var output = "";
      var records = "";
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          // "Content-Type": "application/json",
          "Content-Type": "multipart/form-data",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));
          thisIns.ProgessStart = 0;
          thisIns.OverlayLoadingFlag = false;
          if (status == 4) {
            thisIns.SaveFlag = false;
          } else {
            thisIns.SubmitFlag = false;
          }

          output = response.data.output;
          flag = response.data.flag;
          console.log("output=" + output + ", flag=" + flag);
          records = response.data.records;
          console.log({ records });

          if (flag == 1) {
            thisIns.sweetAlert("success", output, true);
            thisIns.$router.push("/national/lom-award/list");
          } else {
            thisIns.sweetAlert("error", output, false);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.ProgessStart = 0;
          thisIns.OverlayLoadingFlag = false;
          if (status == 4) {
            thisIns.SaveFlag = false;
          } else {
            thisIns.SubmitFlag = false;
          }
        });
    },
  },

  beforeMount() {
    this.refreshPageData();
  },
};
</script>
<style lang="scss">
#natcon-lom-award-edit {
  background-color: #f5f7fa;
  min-height: 100vh;

  .natcon-alert-card {
    border-left: 4px solid #ff0505;
    background: linear-gradient(
      90deg,
      rgba(25, 118, 210, 0.03),
      rgba(255, 255, 255, 0.01)
    );
    border-radius: 8px;
    transition: box-shadow 0.18s ease, transform 0.18s ease;
  }
  .natcon-alert-title {
    font-weight: 700;
    font-size: 1.05rem;
    color: #ff0505;
  }
  .natcon-alert-msg {
    color: #ff0505;
    margin-top: 6px;
    font-size: 0.95rem;
    line-height: 1.35;
  }
  .natcon-alert-extra {
    color: rgba(198, 40, 40, 0.95);
    font-size: 0.92rem;
    line-height: 1.3;
  }

  .natcon-success-card {
    border-left: 4px solid #2e7d32;
    background: linear-gradient(
      90deg,
      rgba(46, 125, 50, 0.04),
      rgba(255, 255, 255, 0.01)
    );
    border-radius: 8px;
    transition: box-shadow 0.18s ease, transform 0.18s ease;
  }
  // HTML Editor Styles
  .html-editor-wrapper {
    border: 1px solid #e0e0e0;
    border-radius: 4px;

    .professional-editor {
      .ql-container {
        min-height: 150px;
        font-family: inherit;
        font-size: 1rem;
      }

      .ql-toolbar {
        border-bottom: 1px solid #e0e0e0;
        background-color: #f8f9fa;
        border-top-left-radius: 4px;
        border-top-right-radius: 4px;
      }
    }

    .character-counter {
      padding: 4px 12px;
      font-size: 0.75rem;
      color: rgba(0, 0, 0, 0.6);
      background-color: #f8f9fa;
      border-bottom-left-radius: 4px;
      border-bottom-right-radius: 4px;
      transition: all 0.3s ease;

      &.error--text {
        color: #f44336;
        background-color: rgba(244, 67, 54, 0.1);
        font-weight: 500;

        .limit-message {
          animation: pulse 1.5s infinite;
          display: inline-block;
        }
      }

      @keyframes pulse {
        0% {
          opacity: 0.6;
        }
        50% {
          opacity: 1;
        }
        100% {
          opacity: 0.6;
        }
      }
    }
  }

  // Professional Overlay
  .professional-overlay {
    .loading-container {
      text-align: center;
      color: white;

      .loading-text {
        font-size: 1.1rem;
        font-weight: 500;
        margin: 0;
      }
    }
  }

  // Main Form Container
  .main-form-container {
    border-radius: 12px !important;
    overflow: hidden;
    background: white;
    box-shadow: 0 6px 30px rgba(0, 0, 0, 0.1) !important;
  }

  // Professional Header Styles
  .professional-form-header {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border-bottom: 1px solid #e3f2fd;

    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      width: 100%;

      .title-section {
        flex: 1;

        .main-title-wrapper {
          display: flex;
          align-items: center;

          .title-text {
            .form-main-title {
              font-size: 1.75rem;
              font-weight: 700;
              color: #2c3e50;
              margin: 0;
              font-family: "Roboto", sans-serif;
              line-height: 1.2;
            }
            .subtitle {
              color: #6b7280;
              font-size: 0.95rem;
              margin-top: 6px;
            }
          }
        }
      }

      .action-buttons {
        display: flex;
        gap: 0.5rem;

        .action-btn {
          font-weight: 500;
          text-transform: none;
          padding: 0 20px;
          height: 40px;

          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }
        }
      }
    }
  }

  // Form Content Styles
  .form-main-content {
    background: #f5f7fa;
  }

  // Form Loading
  .form-loading-section {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;

    .loading-wrapper {
      text-align: center;

      .loading-text {
        font-size: 1.1rem;
        color: #666;
        font-weight: 500;
        margin: 0;
      }
    }
  }

  .award-detail-card {
    border-radius: 12px !important;
    overflow: hidden;

    .award-detail-header {
      background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
      border-bottom: 1px solid #e0e0e0;
      padding: 1rem 1.5rem;

      .section-title {
        font-size: 1.3rem;
        font-weight: 600;
        color: #2c3e50;
      }
    }

    .detail-item {
      margin-bottom: 0.5rem;

      .detail-label {
        font-size: 0.9rem;
        color: #666;
        font-weight: 500;
        margin-bottom: 0.25rem;
      }

      .detail-value {
        font-size: 1.1rem;
        color: #2c3e50;
        font-weight: 500;

        .status-chip {
          font-weight: 500;
          text-transform: none;
        }
      }
    }
  }

  // Criteria Card and attachments styling
  .criteria-card,
  .submit-card {
    border-radius: 12px !important;
    border: 1px solid #e0e0e0;
  }

  .criteria-header {
    background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
    border-bottom: 1px solid #e0e0e0;
    padding: 1.5rem;
    .section-title {
      font-size: 1.3rem;
      font-weight: 600;
      color: #2c3e50;
    }
  }

  .criteria-detail-card {
    display: flex;
    align-items: center;
    background: #fff3e0;
    border: 1px solid #ffb74d;
    border-radius: 8px;
    padding: 1rem;
    .criteria-title {
      font-size: 1.2rem;
      font-weight: 600;
      color: #e65100;
      margin: 0;
    }
  }

  .attachments-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .attachments-grid .attachment-card {
    border-radius: 8px !important;
    position: relative;
    transition: transform 0.2s, box-shadow 0.2s;
  }
  .attachment-actions {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    z-index: 2;
  }

  .event-image {
    border-radius: 4px;
    border: 1px solid #e0e0e0;
    object-fit: cover;
    transition: transform 0.2s;
  }
  .event-image:hover {
    transform: scale(1.05);
  }

  .character-counter {
    padding: 4px 12px;
    font-size: 0.75rem;
    color: rgba(0, 0, 0, 0.6);
    background: #fafafa;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }

  .natcon-alert-card,
  .natcon-success-card {
    border-radius: 8px;
  }

  .submit-section {
    margin-top: 2rem;
    .submit-card {
      border: 2px solid #e3f2fd;
      background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
    }
    .action-buttons {
      display: flex;
      justify-content: center;
      gap: 1rem;
    }
  }

  // Responsive Design
  @media (max-width: 1024px) {
    .professional-form-header .header-content {
      flex-direction: column;
      gap: 1rem;
      .action-buttons {
        align-self: flex-start;
      }
    }
  }
  @media (max-width: 768px) {
    .main-form-container {
      margin: 0.5rem;
      border-radius: 8px !important;
    }
    .professional-form-header {
      padding: 1rem !important;
      .form-main-title {
        font-size: 1.5rem;
      }
    }
    .action-buttons {
      flex-direction: column;
      .submit-btn {
        width: 100%;
        margin: 0.25rem 0 !important;
      }
    }
  }

  /* dialog */
  .v-dialog .v-card {
    border-radius: 12px;
    overflow: hidden;
  }

  /* Additional Attachments Section */
  .attachments-additional-card {
    border-radius: 12px !important;
    border: 1px solid #e0e0e0;
    background: white;
    margin-top: 1.5rem;
  }

  .attachments-additional-header {
    background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
    border-bottom: 1px solid #ffb74d;
    padding: 1.5rem;

    .section-title {
      font-size: 1.3rem;
      font-weight: 600;
      color: #e65100;
    }
  }

  .add-attachment-action-btn {
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.15);
      background-color: rgba(255, 152, 0, 0.1) !important;
    }
  }

  .info-alert {
    background-color: rgba(255, 152, 0, 0.04);
    border: 1px solid rgba(255, 152, 0, 0.2);
    border-radius: 8px;
    padding: 1rem;

    .text-sm {
      font-size: 0.9rem;
      line-height: 1.4;
      color: #424242;
    }
  }

  .empty-state-container {
    text-align: center;
    padding: 2rem;
    border: 2px dashed #ffb74d;
    border-radius: 8px;
    background-color: #fafafa;
    color: #999;

    p {
      margin: 0;
      color: #666;
      font-weight: 500;
    }
  }

  .image-gallery {
    .image-card {
      border-radius: 8px;
      position: relative;
      overflow: hidden;
      transition: all 0.3s ease;
      border: 1px solid #e0e0e0;

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
      }

      .image-delete-action {
        position: absolute;
        top: 0;
        right: 0;
        z-index: 10;
        background-color: rgba(211, 47, 47, 0.9);
        border-radius: 0 0 0 8px;
        padding: 2px;

        .delete-btn {
          transition: all 0.2s ease;

          &:hover {
            background-color: rgba(211, 47, 47, 1) !important;
          }
        }
      }

      .image-preview {
        border-bottom: 1px solid #e0e0e0;
      }

      .image-details {
        background-color: #fafafa;

        .detail-item {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 0.85rem;

          .detail-label {
            font-weight: 600;
            color: #424242;
            min-width: 40px;
          }

          .detail-value {
            color: #666;
            flex: 1;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }
      }
    }
  }

  /* Video Upload Section */
  .video-upload-card {
    border-radius: 12px !important;
    border: 1px solid #e0e0e0;
    background: white;
  }

  .video-upload-header {
    background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
    border-bottom: 1px solid #ef5350;
    padding: 1.5rem;

    .section-title {
      font-size: 1.3rem;
      font-weight: 600;
      color: #c62828;
    }
  }

  .video-info-alert {
    background-color: rgba(211, 47, 47, 0.04);
    border: 1px solid rgba(211, 47, 47, 0.2);
    border-radius: 8px;
    padding: 1rem;
  }

  .upload-wrapper {
    margin-top: 1rem;

    .upload-label {
      display: flex;
      align-items: center;
      font-weight: 600;
      color: #424242;
      margin-bottom: 12px;
    }

    .video-input {
      &:deep(.v-input__slot) {
        min-height: 56px;
        background-color: #f5f5f5;
        border-radius: 8px;
        transition: all 0.2s ease;
      }

      &:deep(input) {
        color: #424242;
        font-size: 0.95rem;
      }

      &:hover:deep(.v-input__slot) {
        background-color: #eeeeee;
      }
    }
  }

  .video-preview-info {
    .video-chip {
      display: flex;
      gap: 6px;
      font-weight: 500;
    }
  }

  /* Responsive Design */
  @media (max-width: 960px) {
    .attachments-additional-header,
    .video-upload-header {
      padding: 1.2rem;

      .section-title {
        font-size: 1.1rem;
      }
    }

    .image-gallery {
      .image-card {
        .image-details {
          .detail-item {
            font-size: 0.8rem;
          }
        }
      }
    }
  }

  @media (max-width: 600px) {
    .attachments-additional-header,
    .video-upload-header {
      padding: 1rem;

      .section-title {
        font-size: 1rem;
      }
    }

    .empty-state-container {
      padding: 1.5rem;

      p {
        font-size: 0.9rem;
      }
    }

    .image-gallery {
      .image-card {
        .image-details {
          .detail-item {
            font-size: 0.75rem;
            flex-direction: column;
            align-items: flex-start;
          }
        }
      }
    }

    .upload-label {
      font-size: 0.95rem;
    }
  }
}
</style>